/**
 * Shop the recipe (Voltage)
 * -----------------------------------------------------------------------------
 * Two jobs, both scoped to the recipe article template — this file is only
 * loaded by snippets/shop-the-recipe-voltage.liquid.
 *
 * 1. Stop add-to-cart bouncing the reader out of the recipe.
 *    The store runs cart_type: "page", and Impact's ProductForm hard-redirects
 *    to /cart on a successful add (theme.js ~2689). cartType is read at click
 *    time in exactly three places, so flipping it to "drawer" for this page
 *    only suppresses the redirect AND lets <cart-drawer> open instead. No
 *    vendor file is touched and no other page is affected.
 *
 * 2. Add-all button, which mirrors ProductForm._onSubmit's event contract so
 *    the cart count, cart drawer and any other listener update exactly as they
 *    do from a normal add.
 */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  // ---- 1. Keep the reader on the page --------------------------------------
  if (window.themeVariables?.settings) {
    window.themeVariables.settings.cartType = 'drawer';
  }

  // ---- 2. Add all ----------------------------------------------------------
  const button = document.querySelector('[data-shop-the-recipe-add-all]');
  if (!button) return;

  const status = document.getElementById(button.getAttribute('aria-describedby'));

  const announce = (message) => {
    if (status) status.textContent = message;
  };

  button.addEventListener('click', async () => {
    let variantIds;

    try {
      variantIds = JSON.parse(button.getAttribute('data-shop-the-recipe-add-all'));
    } catch (error) {
      return;
    }

    if (!Array.isArray(variantIds) || variantIds.length === 0) return;

    const originalLabel = button.textContent;
    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-busy', 'true');
    announce('Adding to cart…');

    try {
      // Let the cart drawer (and anything else) register the sections it wants
      // re-rendered, exactly as the theme's own add-to-cart does.
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Required for Shopify to run its inventory check
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id, quantity: 1 })),
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        announce(responseJson.description || 'Sorry, that could not be added to your cart.');
        button.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson.description }
          })
        );
        return;
      }

      const cart = await (await fetch(`${root()}cart.js`)).json();
      cart.sections = responseJson.sections;

      const added = responseJson.items || [];
      announce(`${added.length} ${added.length === 1 ? 'item' : 'items'} added to your cart.`);

      // Same two events, same shapes, as ProductForm._onSubmit dispatches.
      button.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: added, cart }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        })
      );
    } catch (error) {
      announce('Sorry, something went wrong. Please try again.');
    } finally {
      button.removeAttribute('disabled');
      button.removeAttribute('aria-busy');
      button.textContent = originalLabel;
    }
  });
})();
